import baseService from '../utils/axios';

export const getSubdomain = async () => baseService.get('/zendesk/subdomain');
export const getConfig = async () => baseService.get('/zendesk/config');
export const putConfig = async (newConfig) => baseService.put('/zendesk/config', { new_config: newConfig });

export const putSubdomain = async (subdomain) => baseService.put('/zendesk/subdomain', { subdomain });

export const getAuthUrl = async (service) => baseService.get(`/service_oauth/auth_url/${service}`);

export const storeConnectorToken = async (service, urlParams) =>
  baseService.post(`/service_oauth/callback/${service}`, { url_params: urlParams });

export const exchangeCodeForToken = async (service, code) =>
  baseService.post(`/integrations/exchange_code_for_token`, { 
    service_name: service,
    code
  });

export const removeServiceAuthRequest = async (serviceName) => 
  baseService.post('/service_oauth/remove_auth', { service_name: serviceName });

export const setupZendesk = async () => baseService.post(`/zendesk/setup`);

export const getBillingInfo = async () => baseService.get(`/billing`);

export const getSubscriptionData = async () => baseService.get('/billing/subscription_data');

export const createBillingInfo = async (data) => {
  if ('empty' in data) {
    delete data.empty;
  }
  return await baseService.post(`/billing`, data);
};

export const updateBillingInfo = async (data) => baseService.put(`/billing`, data);

export const getAccountSettings = async () => baseService.get('/account/settings');

export const changeAccLanguage = async (newLang) => baseService.post('/account/change_language', { new_lang: newLang });

export const acceptTermsAndConditions = async ({ termsLanguage, acceptanceSource, browserContext }) =>
  baseService.post('/account/terms_acceptance', {
    terms_language: termsLanguage,
    acceptance_source: acceptanceSource,
    browser_context: browserContext,
  });

export const changeAccStealth = async (stealthData) => {
  return await baseService.post('/account/change_stealth_mode', {
    new_stealth_mode: stealthData.stealthModeOn,
    new_stealth_email: stealthData.stealthEmail,
    is_stealth_email_on: stealthData.isStealthEmailOn
  });
};

export const changeBotStyle = async (styleData) => {
  return await baseService.post('/account/change_bot_style', {
    is_bot_formal: styleData.isBotFormal,
    bot_style_instructions: styleData.botStyleInstructions,
    new_lang: styleData.newLanguage
  });
};

export const getShopifyConfig = async () => baseService.get('/shopify/config');

export const updateShopifyShopUrl = async (shopUrl) =>
  baseService.post('/shopify/update_shop_url', { shop_url: shopUrl });

export const updateIsProcessingDisabled = async (isProcessingDisabled) =>
  baseService.post('/account/change_processing_disabled', { is_processing_disabled: isProcessingDisabled });

export const getIntegrationToken = async (service) => baseService.get(`/service_oauth/get_token/${service}`);

export const getEnvironment = async () => baseService.get('/account/environment');

export const updateActionsConfig = async (actionsConfig) =>
  baseService.post('/account/update_actions_config', { actions_config: actionsConfig });

export const updateProcessingConfig = async (processingConfig) =>
  baseService.post('/account/update_processing_config', { processing_config: processingConfig });

export const changeOneSetting = async (settingsCategory, settingsKey, settingsData) => baseService.post('/account/one_setting', {
  settings_category: settingsCategory,
  settings_key: settingsKey,
  settings_data: settingsData
});

export const updateSubscriptionConfig = async (config) =>
  baseService.put(`/billing/subscription_config`, { subscription_config: config });

export const createStripeCustomer = async () => baseService.post('/billing/create-stripe-customer');

export const attachPaymentMethod = async (paymentMethodId) =>
  baseService.post('/billing/attach-payment-method', { payment_method_id: paymentMethodId });

export const createSubscription = async (planSlug) =>
  baseService.post('/billing/create-subscription', { package: planSlug });

export const getOptInMessage = async () => baseService.get('/account/opt_in_message');

export const setOptInMessageStatusService = async (enabled, customMessage) => {
  try {
    const response = await baseService.post('/account/set_opt_in_status_and_message', {
      enabled,
      custom_message: customMessage
    });
    return { success: true, ...response.data };
  } catch (error) {
    console.error('Error updating opt-in message status:', error);
    return { success: false, error: error.message || 'An error occurred' };
  }
};

export const sendOptInDecision = async (ticketLogId, decision) => {
  return await baseService.post('/processing_log/opt_in_decision', {
    ticket_log_id: ticketLogId,
    decision
  });
};

export const resetOptInMessage = async () => baseService.post('/account/reset_opt_in_message');

export const checkCurrentMessageUsage = async () => baseService.get('/account/current_message_usage');

export const updateIsShopifyMarket = async (isShopifyMarket) => baseService.post('/account/update_is_shopify_market', { 
  is_shopify_market: isShopifyMarket 
});

export const updateIsAngryCheckDisabled = async (isAngryCheckDisabled) => baseService.post('/account/update_is_angry_check_disabled', { 
  is_angry_check_disabled: isAngryCheckDisabled 
});

export const updateCustomConfig = async (key, value) => baseService.post('/account/update_custom_config', { 
  update_key: key,
  update_value: value
});

export const updateAccountMultiAccountConfigUser = async (key, value) => baseService.post('/account/update_multi_account_config', {
  update_key: key, 
  update_value: value
});

export const updateActionsConfigProperty = async (propertyName, propertyValue) => baseService.post('/account/change_actions_config_one_property', {
  property_name: propertyName,
  property_value: propertyValue
});

export const getJtlConfig = async () => baseService.get('/jtl/config');

export const putJtlConfig = async (newConfig) => baseService.put('/jtl/config', { new_config: newConfig });

export const removeJtlConfig = async () => baseService.delete('/jtl/config');

export const initiateJtlRegistration = async (params) => 
  baseService.post('/jtl/register/initiate', {
    api_ip: params.api_ip,
    api_port: params.api_port
  });

export const completeJtlRegistration = async (params) =>
  baseService.post('/jtl/register/complete', {
    api_ip: params.api_ip,
    api_port: params.api_port,
    registration_id: params.registration_id
  });

export const initSpamCheckRules = async () => baseService.post('/account/init_spam_check_rules');

export const updateMultiAccountConfig = async (
  updateKey,
  updateValue
) => baseService.post('/account/update_multi_account_config', { 
  update_key: updateKey,
  update_value: updateValue
});

export const createNewMultiAccount = async (accountName) => baseService.post('/account/create_sub_account', { 
  sub_account_name: accountName,
});

export const changeSubAccountName = async (subAccountUserId, subAccountName) => baseService.post('/account/change_sub_account_name', { 
  sub_account_user_id: subAccountUserId,
  new_sub_account_name: subAccountName,
});

export const removeIntegratedServiceConfig = async (serviceName) => baseService.post('/integrations/remove_service_config', { 
  service_name: serviceName,
});
